import { useEffect, useMemo, useState, type FormEvent } from "react";
import { collection, doc, onSnapshot, orderBy, query, updateDoc } from "firebase/firestore";
import { Package, Trash2 } from "lucide-react";
import { ROLE_RANK, paths, type Job, type JobWorkItem } from "@clevis/shared-types";
import { Button, Card, CardTitle, Input, Label, cn } from "@clevis/shared-ui";
import { db } from "../lib/firebase";
import { call } from "../lib/callFunction";
import { editStamp } from "../lib/editStamp";
import { useAuth } from "../hooks/useAuth";
import { textareaClass } from "./JobEditCard";

// Requested vs. done, on every job and visit. "Requested" is the job's
// description (and its project item); this card is what the tech FOUND and
// what was actually DONE: parts and work logged as they happen. A part from
// the pricebook comes out of stock right away (warranty work too), the job's
// subtotal updates, and every line says who logged it.

export type WorkRow = JobWorkItem & { id: string };
interface FieldItem {
  id: string;
  name: string;
  description: string;
  category: string;
  priceCents: number;
  kind: string;
}
const money = (c: number) => (c / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });

export function useJobWork(tenantId: string | null, jobId: string): WorkRow[] {
  const [rows, setRows] = useState<WorkRow[]>([]);
  useEffect(() => {
    if (!tenantId) return;
    return onSnapshot(
      query(collection(db, paths.tenantJobWorkItems(tenantId, jobId)), orderBy("at")),
      (snap) => setRows(snap.docs.map((d) => ({ id: d.id, ...(d.data() as JobWorkItem) }))),
      () => setRows([]),
    );
  }, [tenantId, jobId]);
  return rows;
}

export function JobWorkDone({ tenantId, jobId, job, work }: { tenantId: string; jobId: string; job: Job; work: WorkRow[] }) {
  const { user, role } = useAuth();
  const isTech = role === "tech";
  const isManager = !!role && ROLE_RANK[role] >= ROLE_RANK.manager;
  const [findings, setFindings] = useState<string | null>(null);
  const [savingFindings, setSavingFindings] = useState(false);
  const [catalog, setCatalog] = useState<FieldItem[] | null>(null);
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState<FieldItem | null>(null);
  const [desc, setDesc] = useState("");
  const [qty, setQty] = useState("1");
  const [price, setPrice] = useState("");
  // Warranty and goodwill visits log what was used without billing it.
  const [billable, setBillable] = useState(job.kind !== "warranty" && job.coverage !== "goodwill");
  const [fromStock, setFromStock] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (catalog !== null) return;
    void call<{ items: FieldItem[] }>("listFieldPricebook", { tenantId }, { mutation: false })
      .then((r) => setCatalog(r.items))
      .catch(() => setCatalog([]));
  }, [tenantId, catalog]);

  const matches = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q || !catalog) return [];
    return catalog.filter((i) => `${i.name} ${i.category} ${i.description}`.toLowerCase().includes(q)).slice(0, 8);
  }, [catalog, search]);
  const billed = work.filter((w) => w.billable).reduce((s, w) => s + Math.round(w.quantity * w.unitPriceCents), 0);

  function pick(i: FieldItem) {
    setPicked(i);
    setDesc(i.name);
    setPrice((i.priceCents / 100).toFixed(2));
    setSearch("");
  }
  function reset() {
    setPicked(null);
    setDesc("");
    setQty("1");
    setPrice("");
    setSearch("");
  }

  async function add(e: FormEvent) {
    e.preventDefault();
    const quantity = parseFloat(qty || "0");
    if (!desc.trim() || !(quantity > 0)) return setError("Say what was done or used, and how many.");
    setBusy("add");
    setError("");
    try {
      await call("addJobWorkItem", {
        tenantId,
        jobId,
        description: desc.trim(),
        quantity,
        ...(price.trim() ? { unitPriceCents: Math.round(parseFloat(price) * 100) } : {}),
        billable,
        itemId: picked?.id ?? null,
        fromStock: !!picked && fromStock,
      });
      reset();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  }
  async function remove(w: WorkRow) {
    setBusy(w.id);
    setError("");
    try {
      await call("removeJobWorkItem", { tenantId, jobId, workItemId: w.id });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  }
  async function saveFindings() {
    if (findings === null || !user) return;
    setSavingFindings(true);
    try {
      await updateDoc(doc(db, paths.tenantJob(tenantId, jobId)), { findings: findings.trim(), ...editStamp(user) });
      setFindings(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSavingFindings(false);
    }
  }

  const canEdit = job.status !== "canceled";
  return (
    <Card>
      <div className="flex items-baseline justify-between gap-2">
        <CardTitle className="mb-0">Found &amp; done</CardTitle>
        <span className="text-xs text-steel">
          {work.length} logged · <strong className="text-ink">{money(billed)}</strong> billable
        </span>
      </div>

      <div className="mt-2">
        <Label>What did you find?</Label>
        <textarea
          className={textareaClass}
          rows={2}
          placeholder="e.g. Called for a door that won't close: the track was fine, a torsion spring was broken."
          value={findings ?? job.findings ?? ""}
          disabled={!canEdit}
          onChange={(e) => setFindings(e.target.value)}
        />
        {findings !== null && findings !== (job.findings ?? "") ? (
          <div className="mt-1 flex gap-2">
            <Button className="px-3 py-1 text-xs" busy={savingFindings} onClick={() => void saveFindings()}>
              Save findings
            </Button>
            <Button variant="ghost" className="px-3 py-1 text-xs" onClick={() => setFindings(null)}>
              Cancel
            </Button>
          </div>
        ) : null}
      </div>

      <div className="mt-3 divide-y divide-steel/10">
        {work.map((w) => (
          <div key={w.id} className="flex items-center justify-between gap-2 py-1 text-sm">
            <span className="min-w-0">
              <span className="text-ink">
                {w.quantity} × {w.description}
              </span>
              <span className="block truncate text-[11px] text-steel">
                {w.addedByName} · {new Date(w.at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                {w.fromStock ? (
                  <span className="text-success">
                    {" "}
                    · <Package size={10} className="inline" /> from stock
                  </span>
                ) : null}
                {!w.billable ? " · not billed" : ""}
                {w.piece ? (
                  <span className={cn(w.piece.status === "pending" ? "text-brandorange-dark" : w.piece.status === "approved" ? "text-success" : "text-steel")}>
                    {" "}
                    · piece pay {w.piece.status === "pending" ? "waiting for a manager's OK" : w.piece.status === "approved" ? `OK'd by ${w.piece.byName || "a manager"}` : "declined"}
                  </span>
                ) : null}
              </span>
            </span>
            <span className="flex shrink-0 items-center gap-2">
              {w.piece?.status === "pending" && isManager ? (
                <>
                  <button className="text-xs font-semibold text-success hover:underline" disabled={busy === w.id} onClick={() => void call("reviewPieceWork", { tenantId, jobId, workItemId: w.id, approved: true })}>
                    OK piece
                  </button>
                  <button className="text-xs text-steel hover:text-danger hover:underline" disabled={busy === w.id} onClick={() => void call("reviewPieceWork", { tenantId, jobId, workItemId: w.id, approved: false })}>
                    Decline
                  </button>
                </>
              ) : null}
              <span className={cn("text-xs", w.billable ? "text-ink" : "text-steel line-through")}>{money(Math.round(w.quantity * w.unitPriceCents))}</span>
              {canEdit && (!isTech || w.addedByUid === user?.uid) ? (
                <button aria-label={`Remove ${w.description}`} className="text-steel hover:text-danger" disabled={busy === w.id} onClick={() => void remove(w)}>
                  <Trash2 size={13} />
                </button>
              ) : null}
            </span>
          </div>
        ))}
        {work.length === 0 ? <p className="py-1 text-xs text-steel">Nothing logged yet. Add parts and work as you go.</p> : null}
      </div>

      {canEdit ? (
        <form onSubmit={add} className="mt-3 space-y-2 rounded-lg bg-cream/60 p-2">
          <div className="relative">
            <Label>Part or work</Label>
            <Input
              placeholder={catalog === null ? "Loading the pricebook…" : "Search the pricebook (e.g. spring), or type anything"}
              value={picked ? desc : search || desc}
              onChange={(e) => {
                setPicked(null);
                setSearch(e.target.value);
                setDesc(e.target.value);
              }}
            />
            {matches.length > 0 && !picked ? (
              <div className="absolute left-0 right-0 top-full z-10 mt-1 rounded-lg border border-steel/30 bg-white shadow">
                {matches.map((i) => (
                  <button key={i.id} type="button" className="block w-full px-2 py-1.5 text-left text-sm hover:bg-cream" onClick={() => pick(i)}>
                    {i.name} <span className="text-xs text-steel">· {money(i.priceCents)}</span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div className="w-20">
              <Label>Qty</Label>
              <Input type="number" min="0" step="any" value={qty} onChange={(e) => setQty(e.target.value)} />
            </div>
            <div className="w-28">
              <Label>Price each</Label>
              <Input type="number" step="0.01" placeholder={picked ? "" : "0.00"} value={price} onChange={(e) => setPrice(e.target.value)} />
            </div>
            <label className="flex items-center gap-1 pb-2 text-xs text-ink">
              <input type="checkbox" checked={billable} onChange={(e) => setBillable(e.target.checked)} /> Bill it
            </label>
            {picked ? (
              <label className="flex items-center gap-1 pb-2 text-xs text-ink">
                <input type="checkbox" checked={fromStock} onChange={(e) => setFromStock(e.target.checked)} /> From stock
              </label>
            ) : null}
            <Button type="submit" className="px-3 py-1.5 text-xs" busy={busy === "add"}>
              Log it
            </Button>
          </div>
        </form>
      ) : null}
      {error ? <p className="mt-1 text-xs text-danger">{error}</p> : null}
    </Card>
  );
}
